import { useState } from "react";
import { adminApi } from "../api/admin";
import type { BulkUpdateRequest, Collection, Member, Visibility } from "../api/types";
import { VisibilityBadge } from "../components/VisibilityBadge";
import { useAsync } from "../hooks/useAsync";
import { VISIBILITY_LABELS, VISIBILITY_OPTIONS } from "../lib/format";
import type { FieldDef } from "./AdminForm";
import type { BulkAction, TypeFilter } from "./AdminTable";
import { parseTags } from "./formUtils";

/**
 * The parts of the Photos and Videos tabs that are the same for both: who can
 * see an item, whether it is on the front page, and the bulk actions that
 * change those for a whole selection at once.
 */

/** Anything with the shared library fields. */
export interface MediaRow {
  id: number;
  title: string;
  url: string;
  thumbnailUrl: string | null;
  tags: string[];
  collection: string;
  featured: boolean;
  visibility: Visibility;
  sharedWith: string[];
}

/** The member list, for "share with" choices; refetched when `token` changes. */
export function useMembers(token = 0): Member[] {
  const { data } = useAsync(() => adminApi.listMembers(), [token]);
  return data ?? [];
}

function memberOptions(members: Member[]) {
  return members.map((m) => ({
    value: m.email,
    label: m.displayName ? `${m.displayName} — ${m.email}` : m.email,
  }));
}

/** Form fields for visibility, and the member checklist shown when it is "specific members". */
export function accessFields(members: Member[]): FieldDef[] {
  return [
    {
      name: "visibility",
      label: "Who can see it",
      type: "select",
      options: VISIBILITY_OPTIONS,
      help: "Hidden items vanish from every page, search, and feed — and their files stop being served — for anyone not allowed to see them.",
    },
    {
      name: "sharedWith",
      label: "Shared with",
      type: "checklist",
      required: true,
      options: memberOptions(members),
      showIf: (v) => v.visibility === "RESTRICTED",
      emptyHint: "No members yet — add people on the Members tab first.",
    },
  ];
}

/** Visibility, as a table cell: a badge for hidden items, a quiet "Public" otherwise. */
export function VisibilityCell({ row }: { row: MediaRow }) {
  if (row.visibility === "PUBLIC") return <span className="cell-muted">Public</span>;
  return (
    <span title={row.sharedWith.length ? `Shared with ${row.sharedWith.join(", ")}` : undefined}>
      <VisibilityBadge visibility={row.visibility} />
      {row.visibility === "RESTRICTED" && (
        <span className="cell-muted"> ×{row.sharedWith.length}</span>
      )}
    </span>
  );
}

/** A small thumbnail for a table row (loads private images too — the admin has a session cookie). */
export function Thumb({ url }: { url: string | null }) {
  if (!url) return <span className="admin-thumb admin-thumb--empty" aria-hidden="true" />;
  return <img className="admin-thumb" src={url} alt="" loading="lazy" />;
}

export function mediaFilters<T extends MediaRow>(collections: Collection[]): TypeFilter<T>[] {
  return [
    {
      label: "Visibility",
      value: (r) => r.visibility,
      options: VISIBILITY_OPTIONS.map((o) => ({ value: o.value, label: VISIBILITY_LABELS[o.value] })),
    },
    {
      label: "Front page",
      value: (r) => (r.featured ? "yes" : "no"),
      options: [
        { value: "yes", label: "On it" },
        { value: "no", label: "Not on it" },
      ],
    },
    {
      label: "Collection",
      value: (r) => r.collection,
      options: collections.map((c) => ({ value: c.id, label: c.label })),
    },
  ];
}

/** Bulk actions that need more input than a click. */
export type BulkDialogKind = "share" | "addTags" | "removeTags" | "collection";

export interface OpenBulkDialog {
  kind: BulkDialogKind;
  ids: number[];
}

/**
 * The bulk actions for a photo or video table. Simple ones call `apply`
 * straight away; the ones that need a choice open the {@link BulkDialog}.
 */
export function mediaBulkActions<T extends MediaRow>(opts: {
  noun: string;
  apply: (change: Omit<BulkUpdateRequest, "ids">, ids: number[]) => Promise<void>;
  openDialog: (dialog: OpenBulkDialog) => void;
  remove: (rows: T[]) => Promise<void>;
}): BulkAction<T>[] {
  const ids = (rows: T[]) => rows.map((r) => r.id);
  const dialog = (kind: BulkDialogKind) => async (rows: T[]) => opts.openDialog({ kind, ids: ids(rows) });
  return [
    { label: "Make public", run: (rows) => opts.apply({ visibility: "PUBLIC" }, ids(rows)) },
    { label: "Members only", run: (rows) => opts.apply({ visibility: "MEMBERS" }, ids(rows)) },
    { label: "Share with…", run: dialog("share") },
    {
      label: "Make private",
      confirm: `Hide the selected ${opts.noun} from everyone but you?`,
      run: (rows) => opts.apply({ visibility: "PRIVATE" }, ids(rows)),
    },
    { label: "Add to front page", run: (rows) => opts.apply({ featured: true }, ids(rows)) },
    { label: "Remove from front page", run: (rows) => opts.apply({ featured: false }, ids(rows)) },
    { label: "Add tags…", run: dialog("addTags") },
    { label: "Remove tags…", run: dialog("removeTags") },
    { label: "Move to collection…", run: dialog("collection") },
    {
      label: "Delete selected",
      danger: true,
      confirm: `Delete all selected ${opts.noun}? When write-through is on, their manifest entries are removed too.`,
      run: opts.remove,
    },
  ];
}

const DIALOG_TITLES: Record<BulkDialogKind, string> = {
  share: "Share with specific members",
  addTags: "Add tags",
  removeTags: "Remove tags",
  collection: "Move to collection",
};

/** The input step of a bulk action: pick members, type tags, or choose a collection. */
export function BulkDialog({
  dialog,
  noun,
  members,
  collections,
  onApply,
  onCancel,
}: {
  dialog: OpenBulkDialog;
  noun: string;
  members: Member[];
  collections: Collection[];
  onApply: (change: Omit<BulkUpdateRequest, "ids">) => Promise<void>;
  onCancel: () => void;
}) {
  const [emails, setEmails] = useState<string[]>([]);
  const [tags, setTags] = useState("");
  const [collection, setCollection] = useState(collections[0]?.id ?? "life");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  function change(): Omit<BulkUpdateRequest, "ids"> | null {
    switch (dialog.kind) {
      case "share":
        return emails.length ? { visibility: "RESTRICTED", sharedWith: emails } : null;
      case "addTags":
        return parseTags(tags).length ? { addTags: parseTags(tags) } : null;
      case "removeTags":
        return parseTags(tags).length ? { removeTags: parseTags(tags) } : null;
      case "collection":
        return { collection };
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const c = change();
    if (!c) return setError(dialog.kind === "share" ? "Pick at least one member." : "Enter at least one tag.");
    setBusy(true);
    setError("");
    try {
      await onApply(c);
    } catch (err) {
      setError(err instanceof Error ? err.message : "That change failed.");
      setBusy(false);
    }
  }

  return (
    <div className="admin-modal__backdrop" role="dialog" aria-modal="true" onMouseDown={onCancel}>
      <div className="admin-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="admin-modal__head">
          <h2>
            {DIALOG_TITLES[dialog.kind]} — {dialog.ids.length} {noun}
          </h2>
        </div>
        <div className="admin-modal__body">
          {error && (
            <p className="notice notice--err" role="alert">
              ✕ {error}
            </p>
          )}
          <form className="form" onSubmit={submit} noValidate style={{ maxWidth: "none" }}>
            {dialog.kind === "share" && (
              <div className="field">
                <label>Only these members will see them</label>
                {members.length === 0 ? (
                  <p className="checklist__empty">No members yet — add people on the Members tab first.</p>
                ) : (
                  <div className="checklist">
                    {members.map((m) => (
                      <label key={m.id} className="checklist__item">
                        <input
                          type="checkbox"
                          style={{ width: "auto" }}
                          checked={emails.includes(m.email)}
                          onChange={(e) =>
                            setEmails((prev) =>
                              e.target.checked ? [...prev, m.email] : prev.filter((x) => x !== m.email),
                            )
                          }
                        />{" "}
                        {m.displayName ? `${m.displayName} — ${m.email}` : m.email}
                      </label>
                    ))}
                  </div>
                )}
                <span className="mono" style={{ fontSize: "0.7rem", color: "var(--ink-500)" }}>
                  Replaces whoever they were shared with before.
                </span>
              </div>
            )}
            {(dialog.kind === "addTags" || dialog.kind === "removeTags") && (
              <div className="field">
                <label htmlFor="bulk-tags">Tags</label>
                <input
                  id="bulk-tags"
                  type="text"
                  value={tags}
                  autoFocus
                  placeholder="Comma-separated, e.g. family, 2024"
                  onChange={(e) => setTags(e.target.value)}
                />
              </div>
            )}
            {dialog.kind === "collection" && (
              <div className="field">
                <label htmlFor="bulk-collection">Collection</label>
                <select id="bulk-collection" value={collection} onChange={(e) => setCollection(e.target.value)}>
                  {collections.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="admin-actions">
              <button type="button" className="btn btn--ghost" onClick={onCancel} disabled={busy}>
                Cancel
              </button>
              <button type="submit" className="btn" disabled={busy}>
                {busy ? "Applying…" : "Apply"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
